import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';

import { ApiService } from '@core/api/api.service';
import {
  CityOption,
  PriceHistoryPoint,
  PriceHistorySeries,
  SimilarProductsSource
} from '@core/models/models';

const CITY_STORAGE_KEY = 'commpare-selected-city';

/** Distinct hues per chain; branches within a chain use shade + dash variations. */
const CHAIN_HUES = [210, 0, 145, 30, 280, 185, 340, 55, 250, 100];

const DASH_PATTERNS = ['', '7 4', '2 3', '10 3 2 3', '1 2', '12 4'];

interface ChartSeries {
  chainId: number;
  chainName: string;
  branchId: number;
  branchName: string;
  color: string;
  dashArray: string;
  changePoints: PriceHistoryPoint[];
  endDate: string;
  endPrice: number;
  isPresent: boolean;
  lastSeenAt: string;
  path: string;
  promoPath: string;
  hasPromo: boolean;
}

interface LegendGroup {
  chainId: number;
  chainName: string;
  chainColor: string;
  items: ChartSeries[];
}

interface AxisTick {
  x: number;
  y: number;
  label: string;
}

interface ChartTooltip {
  x: number;
  y: number;
  below: boolean;
  color: string;
  chainName: string;
  branchName: string;
  dateLabel: string;
  statusLabel: string | null;
  shelfPrice: number;
  promoPrice: number | null;
  promoLabel: string | null;
}

@Component({
  selector: 'app-price-history',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="page">
      <header class="top-bar">
        <div>
          <h2>היסטוריית מחירים</h2>
          <p class="tagline">בחר עיר והזן ברקוד מלא או חלקי (לפחות 4 ספרות)</p>
        </div>
      </header>

      @if (errorMessage) {
        <p class="error" role="alert">{{ errorMessage }}</p>
      }

      <section class="card">
        <form class="search-row" (ngSubmit)="loadHistory()">
          <label class="city-field">
            <span>עיר</span>
            <input
              type="text"
              data-testid="price-history-city"
              [(ngModel)]="cityQuery"
              (ngModelChange)="onCityQueryChange($event)"
              (focus)="openCityMenu()"
              (blur)="closeCityMenuSoon()"
              name="city"
              placeholder="הקלד לחיפוש עיר…"
              autocomplete="off"
              dir="rtl"
              [disabled]="citiesLoading"
            />
            @if (cityMenuOpen && filteredCities.length > 0) {
              <ul class="city-suggestions" role="listbox">
                @for (city of filteredCities; track city.value) {
                  <li>
                    <button type="button" (mousedown)="selectCity(city)">
                      {{ city.label }}
                    </button>
                  </li>
                }
              </ul>
            }
          </label>

          <label>
            <span>ברקוד</span>
            <input
              type="text"
              inputmode="numeric"
              data-testid="price-history-barcode"
              [(ngModel)]="barcodeInput"
              name="barcode"
              placeholder="מלא או חלקי, למשל 16197495"
              autocomplete="off"
              required
            />
          </label>

          <button
            type="submit"
            class="primary"
            data-testid="price-history-search"
            [disabled]="loading || !selectedCity"
          >
            {{ loading ? 'טוען…' : 'הצג גרף' }}
          </button>
        </form>
        @if (selectedCityLabel) {
          <p class="selected-city">עיר נבחרת: {{ selectedCityLabel }}</p>
        }
      </section>

      @if (barcodeMatches.length > 0) {
        <section class="card" data-testid="price-history-matches">
          <h3>נמצאו כמה מוצרים — בחר אחד</h3>
          <ul class="match-list">
            @for (match of barcodeMatches; track match.productId) {
              <li>
                <button type="button" class="match-btn" (click)="selectBarcodeMatch(match)">
                  <strong>{{ match.displayName }}</strong>
                  <span class="meta">ברקוד: {{ match.barcode }}</span>
                  @if (match.brandName) {
                    <span class="meta">{{ match.brandName }}</span>
                  }
                </button>
              </li>
            }
          </ul>
        </section>
      }

      @if (product) {
        <section class="card">
          <h3>מוצר</h3>
          <div class="product-main">
            <strong>{{ product.displayName }}</strong>
            <span class="meta">ברקוד: {{ product.barcode }}</span>
            @if (product.brandName) {
              <span class="meta">{{ product.brandName }}</span>
            }
          </div>
        </section>
      }

      @if (searched && !loading && barcodeMatches.length === 0) {
        <section class="card chart-card">
          <h3>מחיר לפי זמן · לפי סניף</h3>
          <p class="muted chart-note">
            קו עבה = מחיר לתשלום אחרי מבצע ציבורי ידוע. קו דק = מחיר מדף. מבצעים לפי הקובץ
            הנוכחי בלבד (אין היסטוריית מבצעים שנמחקה). קופונים ומועדון לא נכללים.
          </p>
          @if (chartSeries.length === 0) {
            <p class="muted">לא נמצאה היסטוריית מחירים לברקוד זה בעיר שנבחרה.</p>
          } @else {
            <div class="legend" data-testid="price-history-legend">
              @for (group of legendGroups; track group.chainId) {
                <div class="legend-group">
                  <div class="legend-chain">
                    <span class="swatch chain-swatch" [style.background]="group.chainColor"></span>
                    <strong>{{ group.chainName }}</strong>
                  </div>
                  @for (series of group.items; track series.branchId) {
                    <div class="legend-item">
                      <span
                        class="line-sample"
                        [style.borderColor]="series.color"
                        [style.borderStyle]="series.dashArray ? 'dashed' : 'solid'"
                      ></span>
                      <span>
                        {{ series.branchName }}
                        <span class="presence" [class.present]="series.isPresent">
                          {{ series.isPresent ? '· במלאי' : '· לא נראה לאחרונה' }}
                        </span>
                        @if (series.hasPromo) {
                          <span class="promo-flag">· מבצע</span>
                        }
                      </span>
                    </div>
                  }
                </div>
              }
            </div>

            <div class="chart-wrap" data-testid="price-history-chart">
              <svg
                [attr.viewBox]="'0 0 ' + width + ' ' + height"
                role="img"
                aria-label="גרף מחיר לאורך זמן לפי סניף"
              >
                @for (tick of yTicks; track tick.label) {
                  <line
                    class="grid"
                    [attr.x1]="padding.left"
                    [attr.x2]="width - padding.right"
                    [attr.y1]="tick.y"
                    [attr.y2]="tick.y"
                  />
                  <text class="axis-label" [attr.x]="padding.left - 8" [attr.y]="tick.y + 4" text-anchor="end">
                    {{ tick.label }}
                  </text>
                }

                @for (tick of xTicks; track tick.label) {
                  <text
                    class="axis-label"
                    [attr.x]="tick.x"
                    [attr.y]="height - padding.bottom + 18"
                    text-anchor="middle"
                  >
                    {{ tick.label }}
                  </text>
                }

                <line
                  class="axis"
                  [attr.x1]="padding.left"
                  [attr.x2]="padding.left"
                  [attr.y1]="padding.top"
                  [attr.y2]="height - padding.bottom"
                />
                <line
                  class="axis"
                  [attr.x1]="padding.left"
                  [attr.x2]="width - padding.right"
                  [attr.y1]="height - padding.bottom"
                  [attr.y2]="height - padding.bottom"
                />

                @for (series of chartSeries; track series.branchId) {
                  <path
                    class="series-line"
                    [class.shelf-line]="series.hasPromo"
                    [attr.d]="series.path"
                    [attr.stroke]="series.color"
                    [attr.stroke-dasharray]="series.dashArray || null"
                    fill="none"
                  />
                  @if (series.hasPromo) {
                    <path
                      class="series-line promo-line"
                      [attr.d]="series.promoPath"
                      [attr.stroke]="series.color"
                      fill="none"
                    />
                  }
                  @for (point of series.changePoints; track point.date) {
                    <circle
                      class="dot"
                      [attr.cx]="xFor(point.date)"
                      [attr.cy]="yFor(point.price)"
                      r="3.5"
                      [attr.fill]="series.color"
                    />
                    @if (point.promoPrice != null) {
                      <circle
                        class="dot"
                        [attr.cx]="xFor(point.date)"
                        [attr.cy]="yFor(point.promoPrice)"
                        r="3.5"
                        [attr.fill]="series.color"
                      />
                    }
                    <circle
                      class="chart-hit"
                      [attr.cx]="xFor(point.date)"
                      [attr.cy]="yFor(point.price)"
                      r="11"
                      (mouseenter)="showPointTooltip($event, series, point)"
                      (mouseleave)="scheduleHideTooltip()"
                    />
                    @if (point.promoPrice != null) {
                      <circle
                        class="chart-hit"
                        [attr.cx]="xFor(point.date)"
                        [attr.cy]="yFor(point.promoPrice)"
                        r="11"
                        (mouseenter)="showPointTooltip($event, series, point)"
                        (mouseleave)="scheduleHideTooltip()"
                      />
                    }
                  }
                  <circle
                    class="dot"
                    [attr.cx]="xFor(series.endDate)"
                    [attr.cy]="yFor(series.endPrice)"
                    r="4.5"
                    [attr.fill]="series.isPresent ? series.color : '#fff'"
                    [attr.stroke]="series.color"
                    stroke-width="2"
                  />
                  <circle
                    class="chart-hit"
                    [attr.cx]="xFor(series.endDate)"
                    [attr.cy]="yFor(series.endPrice)"
                    r="12"
                    (mouseenter)="showEndTooltip($event, series)"
                    (mouseleave)="scheduleHideTooltip()"
                  />
                }
              </svg>
              @if (chartTooltip; as tip) {
                <div
                  class="chart-tooltip"
                  [class.below]="tip.below"
                  data-testid="price-history-tooltip"
                  [style.left.px]="tip.x"
                  [style.top.px]="tip.y"
                  dir="rtl"
                >
                  <div class="tip-head">
                    <span class="tip-swatch" [style.background]="tip.color"></span>
                    <div class="tip-names">
                      <strong>{{ tip.chainName }}</strong>
                      <span>{{ tip.branchName }}</span>
                    </div>
                  </div>
                  <div class="tip-meta">
                    {{ tip.dateLabel }}
                    @if (tip.statusLabel) {
                      <span>· {{ tip.statusLabel }}</span>
                    }
                  </div>
                  <div class="tip-prices">
                    <div class="tip-row" [class.struck]="tip.promoPrice != null">
                      <span>מדף</span>
                      <strong>{{ formatMoney(tip.shelfPrice) }} ₪</strong>
                    </div>
                    @if (tip.promoPrice != null) {
                      <div class="tip-row pay">
                        <span>אחרי מבצע</span>
                        <strong>{{ formatMoney(tip.promoPrice) }} ₪</strong>
                      </div>
                    }
                  </div>
                  @if (tip.promoLabel) {
                    <p class="tip-label">{{ tip.promoLabel }}</p>
                  }
                </div>
              }
            </div>
          }
        </section>
      }
    </div>
  `,
  styles: [
    `
      .page {
        max-width: 1000px;
        margin: 0 auto;
        padding: 1rem 1rem 2rem;
        box-sizing: border-box;
        display: flex;
        flex-direction: column;
        gap: 1rem;
      }

      .top-bar h2 {
        margin: 0 0 0.25rem;
        font-size: 1.15rem;
      }

      .tagline {
        margin: 0;
        color: #444;
        font-size: 0.95rem;
      }

      .card {
        background: #fff;
        border: 1px solid #e5e5e5;
        border-radius: 12px;
        padding: 1rem;
      }

      .search-row {
        display: flex;
        flex-wrap: wrap;
        gap: 0.75rem;
        align-items: flex-end;
      }

      label {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
        font-size: 0.85rem;
        color: #444;
        min-width: 12rem;
        flex: 1;
        position: relative;
      }

      input {
        font: inherit;
        font-size: 1rem;
        padding: 0.55rem 0.7rem;
        border: 1px solid #ccc;
        border-radius: 8px;
      }

      .city-suggestions {
        position: absolute;
        inset-inline: 0;
        top: calc(100% + 0.2rem);
        z-index: 20;
        margin: 0;
        padding: 0.25rem;
        list-style: none;
        background: #fff;
        border: 1px solid #ddd;
        border-radius: 8px;
        box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
        max-height: 220px;
        overflow-y: auto;
      }

      .city-suggestions button {
        width: 100%;
        text-align: right;
        font: inherit;
        padding: 0.45rem 0.55rem;
        border: none;
        background: transparent;
        border-radius: 6px;
        cursor: pointer;
      }

      .city-suggestions button:hover {
        background: #eef4ff;
      }

      .selected-city {
        margin: 0.65rem 0 0;
        font-size: 0.85rem;
        color: #555;
      }

      .match-list {
        list-style: none;
        margin: 0;
        padding: 0;
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
      }

      .match-btn {
        width: 100%;
        text-align: right;
        font: inherit;
        padding: 0.7rem 0.85rem;
        border: 1px solid #ddd;
        border-radius: 8px;
        background: #fff;
        cursor: pointer;
        display: flex;
        flex-direction: column;
        gap: 0.15rem;
      }

      .match-btn:hover {
        border-color: #0b57d0;
        background: #f5f9ff;
      }

      .primary {
        font: inherit;
        padding: 0.55rem 1rem;
        border: none;
        border-radius: 8px;
        background: #0b57d0;
        color: #fff;
        cursor: pointer;
      }

      .primary:disabled {
        opacity: 0.65;
        cursor: default;
      }

      h3 {
        margin: 0 0 0.75rem;
        font-size: 1rem;
      }

      .product-main {
        display: flex;
        flex-direction: column;
        gap: 0.15rem;
      }

      .meta {
        font-size: 0.82rem;
        color: #555;
      }

      .legend {
        display: flex;
        flex-direction: column;
        gap: 0.85rem;
        margin-bottom: 0.85rem;
      }

      .legend-group {
        display: flex;
        flex-direction: column;
        gap: 0.35rem;
        padding: 0.55rem 0.65rem;
        border: 1px solid #eee;
        border-radius: 8px;
        background: #fafafa;
      }

      .legend-chain {
        display: flex;
        align-items: center;
        gap: 0.4rem;
        margin-bottom: 0.15rem;
        font-size: 0.92rem;
      }

      .legend-item {
        display: flex;
        align-items: center;
        gap: 0.45rem;
        font-size: 0.88rem;
        padding-inline-start: 1.1rem;
      }

      .presence {
        color: #888;
        font-size: 0.78rem;
      }

      .presence.present {
        color: #137333;
      }

      .swatch {
        width: 0.85rem;
        height: 0.85rem;
        border-radius: 3px;
        flex-shrink: 0;
      }

      .chain-swatch {
        width: 0.95rem;
        height: 0.95rem;
      }

      .line-sample {
        width: 1.4rem;
        border-top-width: 3px;
        flex-shrink: 0;
      }

      .chart-wrap {
        position: relative;
        width: 100%;
        overflow-x: auto;
      }

      svg {
        width: 100%;
        min-width: 520px;
        height: auto;
        display: block;
      }

      .dot {
        pointer-events: none;
      }

      .chart-hit {
        fill: transparent;
        cursor: pointer;
      }

      .grid {
        stroke: #eee;
        stroke-width: 1;
      }

      .axis {
        stroke: #bbb;
        stroke-width: 1;
      }

      .axis-label {
        fill: #666;
        font-size: 11px;
      }

      .series-line {
        stroke-width: 2.4;
        stroke-linejoin: round;
        stroke-linecap: round;
      }

      .shelf-line {
        opacity: 0.35;
        stroke-width: 2;
      }

      .promo-line {
        stroke-width: 3;
      }

      .promo-flag {
        color: #b42318;
        font-size: 0.78rem;
      }

      .chart-note {
        margin: -0.35rem 0 0.75rem;
        font-size: 0.82rem;
      }

      .muted {
        color: #666;
        margin: 0;
      }

      .error {
        margin: 0;
        padding: 0.75rem 1rem;
        border-radius: 8px;
        background: #fdecea;
        color: #b00020;
      }

      .chart-tooltip {
        position: fixed;
        z-index: 40;
        min-width: 11.5rem;
        max-width: 16rem;
        padding: 0.65rem 0.75rem;
        border: 1px solid #d8dee8;
        border-radius: 10px;
        background: #fff;
        box-shadow: 0 10px 28px rgba(20, 28, 45, 0.16);
        pointer-events: none;
        transform: translate(-50%, calc(-100% - 12px));
        text-align: right;
      }

      .chart-tooltip.below {
        transform: translate(-50%, 12px);
      }

      .tip-head {
        display: flex;
        align-items: flex-start;
        gap: 0.45rem;
        margin-bottom: 0.35rem;
      }

      .tip-swatch {
        width: 0.7rem;
        height: 0.7rem;
        margin-top: 0.2rem;
        border-radius: 3px;
        flex-shrink: 0;
      }

      .tip-names {
        display: flex;
        flex-direction: column;
        gap: 0.05rem;
        min-width: 0;
      }

      .tip-names strong {
        font-size: 0.88rem;
        line-height: 1.2;
      }

      .tip-names span {
        font-size: 0.8rem;
        color: #555;
      }

      .tip-meta {
        margin-bottom: 0.5rem;
        font-size: 0.75rem;
        color: #6b7280;
      }

      .tip-prices {
        display: flex;
        flex-direction: column;
        gap: 0.2rem;
      }

      .tip-row {
        display: flex;
        justify-content: space-between;
        gap: 0.75rem;
        font-size: 0.84rem;
      }

      .tip-row.struck strong {
        color: #888;
        text-decoration: line-through;
      }

      .tip-row.pay {
        color: #b42318;
      }

      .tip-row.pay strong {
        font-size: 0.95rem;
      }

      .tip-label {
        margin: 0.45rem 0 0;
        padding-top: 0.4rem;
        border-top: 1px solid #eee;
        font-size: 0.72rem;
        color: #5c6570;
        line-height: 1.35;
      }
    `
  ]
})
export class PriceHistoryComponent implements OnInit {
  private readonly api = inject(ApiService);

  readonly width = 860;
  readonly height = 420;
  readonly padding = { top: 20, right: 24, bottom: 42, left: 56 };

  cities: CityOption[] = [];
  filteredCities: CityOption[] = [];
  citiesLoading = false;
  cityQuery = '';
  cityMenuOpen = false;
  selectedCity = '';
  selectedCityLabel = '';

  barcodeInput = '';
  loading = false;
  searched = false;
  errorMessage: string | null = null;
  product: SimilarProductsSource | null = null;
  barcodeMatches: SimilarProductsSource[] = [];
  chartSeries: ChartSeries[] = [];
  legendGroups: LegendGroup[] = [];
  xTicks: AxisTick[] = [];
  yTicks: AxisTick[] = [];
  chartTooltip: ChartTooltip | null = null;
  private hideTooltipTimer: number | null = null;

  private minTime = 0;
  private maxTime = 1;
  private minPrice = 0;
  private maxPrice = 1;

  ngOnInit(): void {
    this.citiesLoading = true;
    this.api.listCities().subscribe({
      next: (response) => {
        this.cities = response.cities;
        this.citiesLoading = false;
        const storedCity = localStorage.getItem(CITY_STORAGE_KEY) ?? '';
        const match = this.cities.find((city) => city.value === storedCity);
        if (match) {
          this.selectCity(match, false);
        }
      },
      error: (err) => {
        this.citiesLoading = false;
        this.errorMessage = this.readError(err);
      }
    });
  }

  onCityQueryChange(value: string): void {
    this.openCityMenu(value);
    const exact = this.cities.find((city) => city.label === value.trim());
    if (exact) {
      this.selectedCity = exact.value;
      this.selectedCityLabel = exact.label;
    } else if (this.selectedCityLabel !== value.trim()) {
      this.selectedCity = '';
      this.selectedCityLabel = '';
    }
  }

  openCityMenu(value = this.cityQuery): void {
    this.cityMenuOpen = true;
    const query = value.trim().toLowerCase();
    this.filteredCities = !query
      ? this.cities.slice(0, 12)
      : this.cities
          .filter((city) => city.label.toLowerCase().includes(query) || city.value.includes(query))
          .slice(0, 12);
  }

  closeCityMenuSoon(): void {
    window.setTimeout(() => {
      this.cityMenuOpen = false;
    }, 150);
  }

  selectCity(city: CityOption, closeMenu = true): void {
    this.selectedCity = city.value;
    this.selectedCityLabel = city.label;
    this.cityQuery = city.label;
    localStorage.setItem(CITY_STORAGE_KEY, city.value);
    if (closeMenu) {
      this.cityMenuOpen = false;
      this.filteredCities = [];
    }
  }

  async loadHistory(exactBarcode?: string): Promise<void> {
    const barcode = (exactBarcode ?? this.barcodeInput).trim();
    if (!this.selectedCity) {
      this.errorMessage = 'בחר עיר מהרשימה.';
      return;
    }
    if (!barcode) {
      this.errorMessage = 'הזן ברקוד.';
      return;
    }
    if (!exactBarcode && barcode.length < 4) {
      this.errorMessage = 'להשלמה חלקית הזן לפחות 4 ספרות, או ברקוד מלא.';
      return;
    }

    this.loading = true;
    this.errorMessage = null;
    this.searched = false;
    this.cityMenuOpen = false;
    this.barcodeMatches = [];
    this.product = null;
    this.chartSeries = [];
    this.legendGroups = [];
    this.chartTooltip = null;

    try {
      const response = await firstValueFrom(
        this.api.getPriceHistory(barcode, this.selectedCity)
      );

      if (response.status === 'ambiguous') {
        this.barcodeMatches = response.matches ?? [];
        this.searched = false;
        if (this.barcodeMatches.length === 0) {
          this.errorMessage = 'נמצאו כמה תוצאות, אך הרשימה ריקה.';
        }
        return;
      }

      if (!response.product || !response.series) {
        this.errorMessage = 'תשובה לא תקינה מהשרת.';
        return;
      }

      this.barcodeInput = response.product.barcode ?? barcode;
      this.product = response.product;
      this.buildChart(response.series);
      this.searched = true;
    } catch (err) {
      this.product = null;
      this.barcodeMatches = [];
      this.chartSeries = [];
      this.legendGroups = [];
      this.chartTooltip = null;
      this.searched = false;
      this.errorMessage = this.readError(err);
    } finally {
      this.loading = false;
    }
  }

  selectBarcodeMatch(match: SimilarProductsSource): void {
    if (!match.barcode) {
      this.errorMessage = 'למוצר זה אין ברקוד.';
      return;
    }
    this.barcodeInput = match.barcode;
    void this.loadHistory(match.barcode);
  }

  xFor(date: string): number {
    const t = new Date(date).getTime();
    const span = Math.max(this.maxTime - this.minTime, 1);
    const plotWidth = this.width - this.padding.left - this.padding.right;
    return this.padding.left + ((t - this.minTime) / span) * plotWidth;
  }

  yFor(price: number): number {
    const span = Math.max(this.maxPrice - this.minPrice, 0.01);
    const plotHeight = this.height - this.padding.top - this.padding.bottom;
    return this.padding.top + (1 - (price - this.minPrice) / span) * plotHeight;
  }

  formatMoney(value: number): string {
    return value.toFixed(2);
  }

  formatDate(value: string): string {
    return new Date(value).toLocaleDateString('he-IL');
  }

  showPointTooltip(
    event: MouseEvent,
    series: ChartSeries,
    point: PriceHistoryPoint
  ): void {
    const payPrice = point.promoPrice ?? point.price;
    this.placeTooltip(
      (event.currentTarget as SVGElement).ownerSVGElement,
      this.xFor(point.date),
      this.yFor(payPrice),
      {
        color: series.color,
        chainName: series.chainName,
        branchName: series.branchName,
        dateLabel: this.formatDate(point.date),
        statusLabel: null,
        shelfPrice: point.price,
        promoPrice: point.promoPrice ?? null,
        promoLabel: point.promoLabel ?? null
      }
    );
  }

  showEndTooltip(event: MouseEvent, series: ChartSeries): void {
    const last = series.changePoints[series.changePoints.length - 1];
    this.placeTooltip(
      (event.currentTarget as SVGElement).ownerSVGElement,
      this.xFor(series.endDate),
      this.yFor(series.endPrice),
      {
        color: series.color,
        chainName: series.chainName,
        branchName: series.branchName,
        dateLabel: this.formatDate(series.endDate),
        statusLabel: series.isPresent ? 'במלאי' : 'לא נראה לאחרונה',
        shelfPrice: last?.price ?? series.endPrice,
        promoPrice: last?.promoPrice ?? null,
        promoLabel: last?.promoLabel ?? null
      }
    );
  }

  scheduleHideTooltip(): void {
    this.clearHideTooltipTimer();
    this.hideTooltipTimer = window.setTimeout(() => {
      this.chartTooltip = null;
      this.hideTooltipTimer = null;
    }, 80);
  }

  private placeTooltip(
    svg: SVGSVGElement | null,
    svgX: number,
    svgY: number,
    content: Omit<ChartTooltip, 'x' | 'y' | 'below'>
  ): void {
    this.clearHideTooltipTimer();
    let x = 0;
    let y = 0;
    const ctm = svg?.getScreenCTM();
    if (ctm) {
      const mapped = new DOMPoint(svgX, svgY).matrixTransform(ctm);
      x = mapped.x;
      y = mapped.y;
    }
    this.chartTooltip = {
      ...content,
      x,
      y,
      below: y < 110
    };
  }

  private clearHideTooltipTimer(): void {
    if (this.hideTooltipTimer != null) {
      window.clearTimeout(this.hideTooltipTimer);
      this.hideTooltipTimer = null;
    }
  }

  private buildChart(series: PriceHistorySeries[]): void {
    const chainOrder = new Map<number, number>();
    const branchIndexInChain = new Map<number, number>();
    const nowIso = new Date().toISOString();

    const prepared = series
      .filter((s) => s.points.length > 0)
      .map((s) => {
        if (!chainOrder.has(s.chainId)) {
          chainOrder.set(s.chainId, chainOrder.size);
        }
        const chainIdx = chainOrder.get(s.chainId)!;
        const branchIdx = branchIndexInChain.get(s.chainId) ?? 0;
        branchIndexInChain.set(s.chainId, branchIdx + 1);

        const changePoints = [...s.points].sort(
          (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
        );
        const endDate = s.isPresent ? nowIso : s.lastSeenAt;
        const last = changePoints[changePoints.length - 1]!;
        const endPrice = last.promoPrice ?? last.price;

        return {
          chainId: s.chainId,
          chainName: s.chainName,
          branchId: s.branchId,
          branchName: s.branchName,
          color: this.colorFor(chainIdx, branchIdx),
          dashArray: DASH_PATTERNS[branchIdx % DASH_PATTERNS.length],
          changePoints,
          endDate,
          endPrice,
          isPresent: s.isPresent,
          lastSeenAt: s.lastSeenAt,
          path: '',
          promoPath: '',
          hasPromo: changePoints.some((p) => p.promoPrice != null)
        };
      });

    if (prepared.length === 0) {
      this.chartSeries = [];
      this.legendGroups = [];
      this.xTicks = [];
      this.yTicks = [];
      return;
    }

    const allTimes = prepared.flatMap((s) => [
      ...s.changePoints.map((p) => new Date(p.date).getTime()),
      new Date(s.endDate).getTime()
    ]);
    const allPrices = prepared.flatMap((s) =>
      s.changePoints.flatMap((p) =>
        p.promoPrice != null ? [p.price, p.promoPrice] : [p.price]
      )
    );

    this.minTime = Math.min(...allTimes);
    this.maxTime = Math.max(...allTimes);
    this.minPrice = Math.min(...allPrices);
    this.maxPrice = Math.max(...allPrices);

    if (this.minTime === this.maxTime) {
      this.minTime -= 24 * 60 * 60 * 1000;
      this.maxTime += 24 * 60 * 60 * 1000;
    }

    const pricePad = Math.max((this.maxPrice - this.minPrice) * 0.08, 0.5);
    this.minPrice = Math.max(0, this.minPrice - pricePad);
    this.maxPrice = this.maxPrice + pricePad;

    this.chartSeries = prepared.map((s) => ({
      ...s,
      path: this.buildStepPath(s.changePoints, s.endDate, s.endPrice, (p) => p.price),
      promoPath: s.hasPromo
        ? this.buildStepPath(
            s.changePoints,
            s.endDate,
            s.endPrice,
            (p) => p.promoPrice ?? p.price
          )
        : ''
    }));

    this.legendGroups = [...chainOrder.entries()].map(([chainId, chainIdx]) => {
      const items = this.chartSeries.filter((s) => s.chainId === chainId);
      return {
        chainId,
        chainName: items[0]?.chainName ?? '',
        chainColor: this.colorFor(chainIdx, 0),
        items
      };
    });

    this.yTicks = this.buildYTicks();
    this.xTicks = this.buildXTicks();
  }

  /** Step line: hold each price until the next change, then to endDate. */
  private buildStepPath(
    changePoints: PriceHistoryPoint[],
    endDate: string,
    endPrice: number,
    priceOf: (point: PriceHistoryPoint) => number = (p) => p.price
  ): string {
    if (changePoints.length === 0) {
      return '';
    }

    const parts: string[] = [];
    const first = changePoints[0]!;
    parts.push(`M${this.xFor(first.date).toFixed(2)} ${this.yFor(priceOf(first)).toFixed(2)}`);

    for (let i = 0; i < changePoints.length - 1; i++) {
      const current = changePoints[i]!;
      const next = changePoints[i + 1]!;
      parts.push(`L${this.xFor(next.date).toFixed(2)} ${this.yFor(priceOf(current)).toFixed(2)}`);
      parts.push(`L${this.xFor(next.date).toFixed(2)} ${this.yFor(priceOf(next)).toFixed(2)}`);
    }

    parts.push(`L${this.xFor(endDate).toFixed(2)} ${this.yFor(endPrice).toFixed(2)}`);
    return parts.join(' ');
  }

  private colorFor(chainIndex: number, branchIndex: number): string {
    const hue = CHAIN_HUES[chainIndex % CHAIN_HUES.length];
    const lightness = 34 + (branchIndex % 5) * 9;
    const saturation = 68 - (branchIndex % 3) * 6;
    return `hsl(${hue} ${saturation}% ${lightness}%)`;
  }

  private buildYTicks(): AxisTick[] {
    const count = 5;
    const ticks: AxisTick[] = [];
    for (let i = 0; i < count; i++) {
      const price = this.minPrice + ((this.maxPrice - this.minPrice) * i) / (count - 1);
      ticks.push({
        x: 0,
        y: this.yFor(price),
        label: `${price.toFixed(1)}`
      });
    }
    return ticks;
  }

  private buildXTicks(): AxisTick[] {
    const count = 5;
    const ticks: AxisTick[] = [];
    for (let i = 0; i < count; i++) {
      const t = this.minTime + ((this.maxTime - this.minTime) * i) / (count - 1);
      const iso = new Date(t).toISOString();
      ticks.push({
        x: this.xFor(iso),
        y: 0,
        label: this.formatDate(iso)
      });
    }
    return ticks;
  }

  private readError(err: unknown): string {
    if (err && typeof err === 'object' && 'error' in err) {
      const body = (err as { error?: unknown }).error;
      if (
        body &&
        typeof body === 'object' &&
        'error' in body &&
        typeof (body as { error: unknown }).error === 'string'
      ) {
        return (body as { error: string }).error;
      }
    }
    return 'משהו השתבש. נסה שוב.';
  }
}
